import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { ExtendedOrderPropertyOption, ExtendedPropertyDef } from '@euler/model';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import {
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetTextInput,
} from '@gorhom/bottom-sheet';
import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform, ScrollView, TouchableOpacity, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { BehaviorSubject } from 'rxjs';
import { useExtendedPropertyModal } from '../functions/useExtendedPropertyModal';

export type ExtendedPropertyContextProps = {
  property$: BehaviorSubject<ExtendedPropertyDef | undefined>;
};

export const ExtendedPropertyContext =
  createContext<ExtendedPropertyContextProps>(null as any);

export const useExtendedPropertyContext = () =>
  useContext(ExtendedPropertyContext);

export const ExtendedPropertyContextProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const property$ = useCreation(
      () => new BehaviorSubject<ExtendedPropertyDef | undefined>(undefined),
      [],
    );
    const value = useMemo(() => ({ property$ }), [property$]);
    return (
      <ExtendedPropertyContext.Provider value={value}>
        {children}
      </ExtendedPropertyContext.Provider>
    );
  },
);

export const ExtendedPropertyOptionModal = memo(() => {
  const { navBarHeight, safeAreaInsets: insets } = useSystemMetrics();

  const { rpx } = useResponsiveContext();

  const {
    visible,
    propertyDef,
    property,
    options,
    selected,
    onSelect,
    onRemarkChange,
    onDismiss,
  } = useExtendedPropertyModal();

  const { bottomSheetModalRef, ...bottomSheetProps } =
    useGeneralBottomSheetModal({
      show: visible,
      topMargin: navBarHeight,
      minSnapPoint: '50%',
      mediumSnapPoint: '75%',
      index: 1,
      enableDismissOnClose: true,
      enablePanDownToClose: true,
      dismissWhenPressBackdrop: true,
      onDismiss,
    });
  const animatedPosition = useSharedValue(0);

  const Header = useCallback(
    () => (
      <Center
        css={`
          padding: ${rpx(10)}px 0;
          border-bottom-width: 1px;
          border-bottom-color: #f1f1f1;
          margin-bottom: ${rpx(15)}px;
        `}
      >
        <Typography.Label size={rpx(16)} color="#333" bold>
          {propertyDef?.name ?? ''}
        </Typography.Label>
      </Center>
    ),
    [propertyDef?.name, rpx],
  );

  // Fix: BottomSheet specific scroll view components do not work on Web right now.
  // https://github.com/gorhom/react-native-bottom-sheet/issues/11
  const ScrollComponent =
    Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

  return (
    <BottomSheetModal
      {...bottomSheetProps}
      animatedPosition={animatedPosition}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="none"
      ref={bottomSheetModalRef}
    >
      <ScrollComponent
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        contentContainerStyle={{
          paddingBottom: insets.bottom + rpx(15),
        }}
      >
        <Header />
        {options.map((option, index) => (
          <Cell
            key={index}
            type={
              propertyDef?.type === 'select' && !propertyDef?.multi
                ? 'radio'
                : 'check'
            }
            option={option}
            onPress={onSelect}
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            selected={selected.includes(option.value || option.defaultValue)}
          />
        ))}
        {propertyDef?.type === 'select' && propertyDef.remark && (
          <RemarkInput remark={property?.remark} onChange={onRemarkChange} />
        )}
      </ScrollComponent>
    </BottomSheetModal>
  );
});

const RemarkInput = memo(
  ({
    remark,
    onChange,
  }: {
    remark?: string;
    onChange?: (remark: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const [remarkText, setRemarkText] = useState(remark ?? '');

    useEffect(() => {
      setRemarkText(remark ?? '');
    }, [remark]);

    const done = usePersistFn(() => {
      onChange?.(remarkText);
    });

    return (
      <BottomSheetTextInput
        multiline
        numberOfLines={4}
        placeholder="备注"
        value={remarkText}
        onChangeText={setRemarkText}
        onSubmitEditing={done}
        onBlur={done}
        css={`
          border-width: 1px;
          border-color: #ddd;
          padding: ${rpx(4)}px;
          height: ${rpx(80)}px;
          margin: ${rpx(15)}px;
          border-radius: ${rpx(5)}px;
        `}
      />
    );
  },
);

const Cell = memo(
  ({
    type,
    option,
    selected,
    onPress,
  }: {
    type: 'radio' | 'check';
    option: ExtendedOrderPropertyOption;
    selected?: boolean;
    onPress?: (option: ExtendedOrderPropertyOption) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(option);
    }, [onPress, option]);
    return (
      <TouchableOpacity
        onPress={handlePress}
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding: ${rpx(4)}px ${rpx(15)}px;
          min-height: ${rpx(36)}px;
        `}
      >
        <Icon
          name={
            selected
              ? type === 'check'
                ? 'checkbox'
                : 'radio_button_on'
              : type === 'check'
              ? 'checkbox_outline'
              : 'radio_button_off'
          }
          color={selected ? '#207FE7' : '#777'}
          size={rpx(20)}
          css={`
            flex-shrink: 0;
            margin-right: ${rpx(5)}px;
          `}
        />
        <View
          css={`
            flex: 1;
          `}
        >
          <Typography.Label
            regular
            size={rpx(16)}
            color={'#333'}
            lineHeight={rpx(20)}
          >
            {option.label}
          </Typography.Label>
          {option.description ? (
            <Typography.Label
              light
              size={rpx(12)}
              color={'#777'}
              lineHeight={rpx(16)}
              css={`
                margin-top: ${rpx(4)}px;
              `}
            >
              {option.description}
            </Typography.Label>
          ) : null}
        </View>
      </TouchableOpacity>
    );
  },
);
